import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { auth } from './api/client';
import { ThemeToggle } from './components/ThemeToggle';
import { AttemptListPage } from './pages/AttemptListPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { BatchPage } from './pages/BatchPage';
import { ClassSectionPage } from './pages/ClassSectionPage';
import { ClassSectionDetailsPage } from './pages/ClassSectionDetailsPage';
import { DepartmentsPage } from './pages/DepartmentsPage';
import { ExamAuthorPage } from './pages/ExamAuthorPage';
import { ExamPaperPrintPage } from './pages/ExamPaperPrintPage';
import { ExamListPage } from './pages/ExamListPage';
import { ExamRuntimePage } from './pages/ExamRuntimePage';
import { ExamSchedulePage } from './pages/ExamSchedulePage';
import { ExamPeriodDetailPage } from './pages/ExamPeriodDetailPage';
import { StudentSchedulePage } from './pages/StudentSchedulePage';
import { InstitutionsPage } from './pages/InstitutionsPage';
import { LoginPage } from './pages/LoginPage';
import { OrganizationsPage } from './pages/OrganizationsPage';
import { ProgramPage } from './pages/ProgramPage';
import { AcademicYearPage } from './pages/AcademicYearPage';
import { BatchYearPage } from './pages/BatchYearPage';
import { SemesterPage } from './pages/SemesterPage';
import { ReviewAttemptPage } from './pages/ReviewAttemptPage';
import { StaffHomePage } from './pages/StaffHomePage';
import { StaffLoginPage } from './pages/StaffLoginPage';
import { StaffPage } from './pages/StaffPage';
import { StaffRolePage } from './pages/StaffRolePage';
import { StudentDashboardPage } from './pages/StudentDashboardPage';
import { StudentResultPage } from './pages/StudentResultPage';
import { SyllabusPage } from './pages/SyllabusPage';
import { StudentLoginPage } from './pages/StudentLoginPage';
import { StudentPage } from './pages/StudentPage';

function RequireAuth({ children }: { children: JSX.Element }) {
  const loc = useLocation();
  if (!auth.getToken()) return <Navigate to="/login" replace />;
  // Force a first-time password change before any other page.
  if (auth.getMustChangePassword() && loc.pathname !== '/change-password')
    return <Navigate to="/change-password" replace />;
  return children;
}

/** Where a staff user lands after sign-in: their org workspace (Institution page). */
function staffHome() {
  const { organizationId } = auth.getPermissions();
  return organizationId ? `/org/${organizationId}/institutions` : '/staff';
}

/** The bare org Exams URL has no page of its own — send it to the first tab (Draft Exams). */
function ExamsIndexRedirect() {
  const { orgId } = useParams();
  return <Navigate to={`/org/${orgId}/exams/drafts`} replace />;
}

/** The /exams space serves both students (new dashboard) and faculty (classic list). */
function StudentExams({ tab }: { tab: 'exams' | 'results' | 'reeval' }) {
  const role = auth.getUser()?.role;
  if (role === 'Student') return <StudentDashboardPage tab={tab} />;
  return <ExamListPage mode={tab === 'exams' ? 'all' : 'attempted'} />;
}

/** Student timetable (published exam schedules for their batch). */
function StudentScheduleRoute() {
  const role = auth.getUser()?.role;
  if (role === 'Student') return <StudentSchedulePage />;
  return <Navigate to="/exams" replace />;
}

/** Send each role to its home: admins manage orgs, staff get their first page, everyone else exams. */
function RoleHome() {
  if (!auth.getToken()) return <Navigate to="/login" replace />;
  if (auth.getMustChangePassword()) return <Navigate to="/change-password" replace />;
  const role = auth.getUser()?.role;
  const home = role === 'Admin' ? '/platform/organizations' : role === 'Staff' ? staffHome() : '/exams';
  return <Navigate to={home} replace />;
}

export default function App() {
  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/change-password" element={<RequireAuth><ChangePasswordPage /></RequireAuth>} />
        <Route path="/staff" element={<RequireAuth><StaffHomePage /></RequireAuth>} />

        {/* Platform admin + staff (org workspace; OrgLayout redirects non-admin/staff away,
            and the API scopes staff to their own institution). */}
        <Route path="/platform/organizations" element={<RequireAuth><OrganizationsPage /></RequireAuth>} />
        <Route path="/org/:orgId" element={<RequireAuth><InstitutionsPage /></RequireAuth>} />
        <Route path="/org/:orgId/institutions" element={<RequireAuth><InstitutionsPage /></RequireAuth>} />
        <Route path="/org/:orgId/departments" element={<RequireAuth><DepartmentsPage /></RequireAuth>} />
        <Route path="/org/:orgId/programs" element={<RequireAuth><ProgramPage /></RequireAuth>} />
        <Route path="/org/:orgId/class-sections" element={<RequireAuth><ClassSectionPage /></RequireAuth>} />
        <Route path="/org/:orgId/class-section-details" element={<RequireAuth><ClassSectionDetailsPage /></RequireAuth>} />
        <Route path="/org/:orgId/academic-years" element={<RequireAuth><AcademicYearPage /></RequireAuth>} />
        <Route path="/org/:orgId/batch-years" element={<RequireAuth><BatchYearPage /></RequireAuth>} />
        <Route path="/org/:orgId/semesters" element={<RequireAuth><SemesterPage /></RequireAuth>} />
        <Route path="/org/:orgId/batches" element={<RequireAuth><BatchPage /></RequireAuth>} />
        <Route path="/org/:orgId/staff-roles" element={<RequireAuth><StaffRolePage /></RequireAuth>} />
        <Route path="/org/:orgId/staff" element={<RequireAuth><StaffPage /></RequireAuth>} />
        <Route path="/org/:orgId/staff-logins" element={<RequireAuth><StaffLoginPage /></RequireAuth>} />
        <Route path="/org/:orgId/students" element={<RequireAuth><StudentPage /></RequireAuth>} />
        <Route path="/org/:orgId/student-logins" element={<RequireAuth><StudentLoginPage /></RequireAuth>} />
        <Route path="/org/:orgId/syllabi" element={<RequireAuth><SyllabusPage /></RequireAuth>} />
        <Route path="/org/:orgId/exam-schedule" element={<RequireAuth><ExamSchedulePage /></RequireAuth>} />
        <Route path="/org/:orgId/exam-schedule/period" element={<RequireAuth><ExamPeriodDetailPage /></RequireAuth>} />
        <Route path="/org/:orgId/exams" element={<RequireAuth><ExamsIndexRedirect /></RequireAuth>} />
        <Route path="/org/:orgId/exams/drafts" element={<RequireAuth><ExamListPage mode="draft" /></RequireAuth>} />
        <Route path="/org/:orgId/exams/published" element={<RequireAuth><ExamListPage mode="published" /></RequireAuth>} />
        <Route path="/org/:orgId/exams/to-evaluate" element={<RequireAuth><ExamListPage mode="toEvaluate" /></RequireAuth>} />
        <Route path="/org/:orgId/exams/results" element={<RequireAuth><ExamListPage mode="results" /></RequireAuth>} />
        <Route path="/org/:orgId/exams/:examId" element={<RequireAuth><ExamAuthorPage /></RequireAuth>} />
        <Route path="/org/:orgId/exams/:examId/paper" element={<RequireAuth><ExamPaperPrintPage /></RequireAuth>} />
        <Route path="/org/:orgId/exams/:examId/review" element={<RequireAuth><AttemptListPage /></RequireAuth>} />
        <Route path="/org/:orgId/exams/:examId/review/:attemptId" element={<RequireAuth><ReviewAttemptPage /></RequireAuth>} />

        {/* Faculty / students */}
        <Route path="/exams" element={<RequireAuth><StudentExams tab="exams" /></RequireAuth>} />
        <Route path="/exams/schedule" element={<RequireAuth><StudentScheduleRoute /></RequireAuth>} />
        <Route path="/exams/results" element={<RequireAuth><StudentExams tab="results" /></RequireAuth>} />
        <Route path="/exams/reeval" element={<RequireAuth><StudentExams tab="reeval" /></RequireAuth>} />
        <Route path="/exams/attempted" element={<RequireAuth><StudentExams tab="results" /></RequireAuth>} />
        <Route path="/exam/:examId" element={<RequireAuth><ExamRuntimePage /></RequireAuth>} />
        <Route path="/exam/:examId/result" element={<RequireAuth><StudentResultPage /></RequireAuth>} />
        <Route path="/exam/:examId/review" element={<RequireAuth><AttemptListPage /></RequireAuth>} />
        <Route path="/exam/:examId/review/:attemptId" element={<RequireAuth><ReviewAttemptPage /></RequireAuth>} />
        <Route path="/author/:examId" element={<RequireAuth><ExamAuthorPage /></RequireAuth>} />
        <Route path="/author/:examId/paper" element={<RequireAuth><ExamPaperPrintPage /></RequireAuth>} />
        <Route path="/author" element={<RequireAuth><ExamAuthorPage /></RequireAuth>} />

        <Route path="*" element={<RoleHome />} />
      </Routes>
      <ThemeToggle />
    </>
  );
}
